/**
 * trapInterpreter.ts — MER Algo Footprints v8.2.0
 *
 * Deterministic Trap Engine interpretation layer.
 *
 * Combines:
 *   - Existing candle-based TrapSignal[] (from trapEngine.ts)
 *   - GammaV2Interpretation (structural gamma signals)
 *   - GammaLiquidityOutput (wall validation, breakout/compression quality)
 *   - SMCResult (BOS, CHOCH, sweep, displacement, FVG, bias)
 *   - RegimeResult (dominant regime, phase, rvol, VWAP distance)
 *
 * Produces TrapEngineOutput — structured, explainable, replay-safe.
 *
 * ── Design principles ─────────────────────────────────────────────────────────
 *
 * 1. Deterministic  — same inputs always produce same output
 * 2. Additive       — does not replace TrapSignal[]; enriches interpretation
 * 3. Honest         — only uses data that exists; no invented precision
 * 4. Explainable    — named rules, stable reasoning order
 * 5. Replay-safe    — pure function, no I/O, no side effects
 * 6. Fail-soft      — all inputs optional; degrades gracefully without gamma
 *
 * ── Trap classification hierarchy ────────────────────────────────────────────
 *
 * Priority (highest wins when multiple candidates):
 *   1. breakout_bull_trap     — upside move likely to reverse
 *   2. breakdown_bear_trap    — downside move likely to reverse
 *   3. flip_zone_trap         — near gamma flip zone, direction ambiguous
 *   4. pin_release_trap       — compression release lacks structural quality
 *   5. false_reversal_trap    — reversal signal but continuation is still favored
 *   6. failed_continuation_trap — move extended but losing structural support
 *   7. none                   — no trap condition identified
 *
 * ── Conflict resolution ───────────────────────────────────────────────────────
 *
 * When multiple trap types are scored simultaneously:
 *   - Both bull and bear traps at flip zone → flip_zone_trap wins
 *   - breakout_bull and breakdown_bear simultaneously → higher score wins
 *   - false_reversal and failed_continuation simultaneously → false_reversal wins
 *     (reversal being wrong is more dangerous than extension being weak)
 *
 * ── Reasoning order (stable) ─────────────────────────────────────────────────
 *
 * Section 1: Current regime / gamma context
 * Section 2: Relevant key structure / wall / flip zone
 * Section 3: Liquidity / validation behavior
 * Section 4: Resolved trap interpretation and rule
 * Section 5: Confidence / caution note
 *
 * ── Replay compatibility ──────────────────────────────────────────────────────
 *
 * All inputs are serializable types from existing engines.
 * Output is fully reproducible from saved inputs.
 * No runtime-only state is required.
 */

import type { TrapSignal, SMCResult, RegimeResult } from '../../../../shared/types/index';
import type { GammaV2Interpretation } from '../gamma/gammaV2Interpreter';
import type { GammaLiquidityOutput } from '../gamma/gammaLiquidityInterpreter';
import type { VolatilityContext } from '../volatility/volatilityInterpreter';
import {
  applyVolTrapModifier, applyVolConfidenceModifier, applyVolRegimeConfModifier,
  regimeToImpact,
} from '../volatility/volatilityInterpreter';

// ── Centralized threshold configuration ──────────────────────────────────────
//
// All tunable thresholds live here.
// Naming conventions:
//   *_PCT   — fraction of spot price (0–1)
//   *_MIN   — minimum score threshold (0–1)
//   *_MAX   — maximum score threshold (0–1)
//   *_SCORE — contribution score value

export const TRAP_ENGINE_THRESHOLDS = {
  // ── Flip zone proximity ────────────────────────────────────────────────────
  // At or within this fraction of spot from flip midpoint → flip zone trap eligible
  FLIP_ZONE_TRAP_PCT:              0.015,  // 1.5% from flip midpoint

  // ── Wall proximity ─────────────────────────────────────────────────────────
  VALIDATED_WALL_PROXIMITY_PCT:    0.015,  // within 1.5% of spot = "near wall"

  // ── Trap risk score thresholds ────────────────────────────────────────────
  HIGH_TRAP_RISK_MIN:              0.65,   // score ≥ this → 'high' overall trapRisk
  MED_TRAP_RISK_MIN:               0.35,   // score ≥ this → 'medium'

  // ── Breakout validity thresholds ──────────────────────────────────────────
  VALID_BREAKOUT_MIN:              0.65,   // score ≥ this → breakoutValidity 'high'
  WEAK_BREAKOUT_MIN:               0.35,   // score ≥ this → 'medium'

  // ── Breakdown validity thresholds ─────────────────────────────────────────
  VALID_BREAKDOWN_MIN:             0.65,
  WEAK_BREAKDOWN_MIN:              0.35,

  // ── Continuation quality ──────────────────────────────────────────────────
  FAILED_CONTINUATION_MAX_QUALITY: 0.40,   // quality below this → failed_continuation eligible
  CONTINUATION_HIGH_MIN:           0.65,

  // ── Reversal quality ──────────────────────────────────────────────────────
  FALSE_REVERSAL_MIN_SCORE:        0.50,   // reversal suspect above this trap score
  REVERSAL_HIGH_MIN:               0.65,

  // ── Pin release ───────────────────────────────────────────────────────────
  FRAGILE_RELEASE_MAX:             0.45,   // release quality below this → pin_release trap

  // ── Regime confidence gate ────────────────────────────────────────────────
  MIN_REGIME_CONF_FOR_UPGRADE:     0.45,   // gamma regime must be at least this confident
                                            // for its signals to upgrade trap classification

  // ── SMC displacement freshness ────────────────────────────────────────────
  DISPLACEMENT_FRESH_MIN:          0.50,   // displacement freshness ≥ this = fresh BOS/CHOCH

  // ── RVOL thresholds ───────────────────────────────────────────────────────
  RVOL_WEAK_BREAKOUT:              1.10,   // breakout with RVOL below this = weak volume
  RVOL_STRONG_MOVE:                1.50,   // RVOL above this = strong institutional participation
} as const;

const T = TRAP_ENGINE_THRESHOLDS;

// ── Input types ───────────────────────────────────────────────────────────────

export interface TrapInterpreterInputs {
  symbol:     string;
  spot:       number;
  /** Existing candle-based trap signals from trapEngine.detectTraps() */
  traps:      TrapSignal[];
  /** Optional — from gammaV2Interpreter.interpretGammaV2() */
  gammaInterp?: GammaV2Interpretation | null;
  /** Optional — from gammaLiquidityInterpreter.interpretGammaLiquidity() */
  liqOutput?:  GammaLiquidityOutput | null;
  /** Optional — from smcEngine */
  smc?:        SMCResult | null;
  /** Optional — from regimeEngine */
  regime?:     RegimeResult | null;
  /** Optional — from volatilityContext (VIX / fear-factor regime) */
  volContext?:  VolatilityContext | null;
}

// ── Output types ──────────────────────────────────────────────────────────────

export type TrapClassification =
  | 'none'
  | 'breakout_bull_trap'
  | 'breakdown_bear_trap'
  | 'failed_continuation_trap'
  | 'false_reversal_trap'
  | 'pin_release_trap'
  | 'flip_zone_trap';

export type TrapSideAssessment = 'longs_at_risk' | 'shorts_at_risk' | 'both' | 'none';
export type QualityLevel = 'high' | 'medium' | 'low';
export type RiskLevel    = 'low' | 'medium' | 'high';

export interface TrapEngineOutput {
  symbol: string;
  asOf:   string;

  trapClassification: TrapClassification;
  trapSide:           TrapSideAssessment;

  /** The primary rule that triggered the classification */
  appliedRule: string;

  /**
   * Intensity of the trap signal.
   *   strong   — high trapRisk + ≥2 aligned signals + high confidence
   *   moderate — medium signals OR moderate confidence
   *   weak     — conflicting signals OR low confidence OR single-source
   */
  trapStrength: 'weak' | 'moderate' | 'strong';

  signals: {
    breakoutValidity:        QualityLevel;
    breakdownValidity:       QualityLevel;
    continuationQuality:     QualityLevel;
    reversalQuality:         QualityLevel;
    releaseQuality:          QualityLevel;
    whipsawRisk:             RiskLevel;
    trapRisk:                RiskLevel;
  };

  structure: {
    keyLevel:          number | null;
    nearestWallAbove:  number | null;
    nearestWallBelow:  number | null;
    flipZoneLow:       number | null;
    flipZoneHigh:      number | null;
  };

  regimeContext: {
    regime:           string;
    directionalBias:  string;
    gammaPosture:     string;
  };

  /** 0–1, weighted combination of all input confidences */
  confidence:       number;
  regimeConfidence: number | null;

  /**
   * Stable ordering:
   *   1. Regime/gamma context
   *   2. Key structure/wall/flip zone
   *   3. Liquidity/validation behavior
   *   4. Resolved trap interpretation
   *   5. Confidence/caution note
   */
  reasoning: string[];

  /**
   * Volatility regime context — null when volContext not provided.
   * regime: classified VIX regime
   * impact: human-readable impact label (low_risk | balanced | elevated_risk | high_instability)
   */
  volatilityContext: {
    regime: import('../volatility/volatilityInterpreter').VolatilityRegime;
    impact: import('../volatility/volatilityInterpreter').VolatilityImpact;
  } | null;

  meta: {
    fallbackActive:      boolean;
    gammaConfidence:     number;
    liquidityConfidence: number;
    smcConfidence:       number;
    triggerSources:      string[];    // which input sources contributed to classification
    candleTrapsActive:   number;      // count of candle-based traps from trapEngine
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _round2(v: number): number { return Math.round(v * 100) / 100; }
function _clamp(v: number, lo: number, hi: number): number { return Math.max(lo, Math.min(hi, v)); }

function _scoreToQuality(score: number): QualityLevel {
  return score >= T.VALID_BREAKOUT_MIN ? 'high' : score >= T.WEAK_BREAKOUT_MIN ? 'medium' : 'low';
}

function _scoreToRisk(score: number): RiskLevel {
  return score >= T.HIGH_TRAP_RISK_MIN ? 'high' : score >= T.MED_TRAP_RISK_MIN ? 'medium' : 'low';
}

// ── Step 1: Score breakout validity ──────────────────────────────────────────
//
// RULE_BREAKOUT_V1: Strong gamma breakoutBias + fake/weak wall + negative gamma regime
//   → breakout validity HIGH (real break confirmed by gamma+liquidity)
//
// RULE_BREAKOUT_V2: Breakout direction conflicts with validated gamma wall
//   → breakout validity LOW (structural resistance likely to hold)
//
// RULE_BREAKOUT_V3: SMC BOS in same direction as breakout + displacement fresh
//   → score boost (structural confirmation)
//
// RULE_BREAKOUT_V4: Weak RVOL on breakout
//   → score penalty (low institutional participation)

function _scoreBreakoutValidity(
  inputs: TrapInterpreterInputs,
  dir: 'up' | 'down',
): { score: number; rules: string[] } {
  const { gammaInterp, liqOutput, smc, regime } = inputs;
  const rules: string[] = [];
  let score = 0.50; // neutral baseline

  if (gammaInterp && gammaInterp.confidence >= T.MIN_REGIME_CONF_FOR_UPGRADE) {
    // Gamma breakout signal
    if (gammaInterp.signals.breakoutBias) {
      const gammaDir = gammaInterp.directionalBias;
      if ((dir === 'up' && gammaDir === 'bullish') || (dir === 'down' && gammaDir === 'bearish')) {
        score += 0.20;
        rules.push('RULE_BREAKOUT_V1a: gamma breakoutBias aligned with direction');
      }
    }
    // Validated wall in breakout direction weakens the move
    if (liqOutput) {
      const wallsInDir = liqOutput.wallValidation.filter(w =>
        (dir === 'up'   && w.side === 'resistance') ||
        (dir === 'down' && w.side === 'support')
      );
      const hasValidatedWall = wallsInDir.some(w => w.status === 'validated');
      const hasFakeWall      = wallsInDir.every(w => w.status === 'fake_risk' || w.status === 'weak');
      if (hasValidatedWall) {
        score -= 0.20;
        rules.push('RULE_BREAKOUT_V2: validated wall in breakout direction — structural resistance likely');
      }
      if (hasFakeWall && wallsInDir.length > 0) {
        score += 0.15;
        rules.push('RULE_BREAKOUT_V1b: only weak/fake walls in breakout direction — path is clear');
      }
      // Liquidity breakout confirmation
      if (liqOutput.interactionSummary.breakoutValidation === 'confirmed') {
        score += 0.15;
        rules.push('RULE_BREAKOUT_V1c: gamma+liquidity breakout confirmed');
      } else if (liqOutput.interactionSummary.breakoutValidation === 'rejected') {
        score -= 0.25;
        rules.push('RULE_BREAKOUT_V2b: gamma+liquidity breakout rejected by absorption behavior');
      }
    }
    // Unstable regime supports breakout
    if (gammaInterp.regime === 'unstable') {
      score += 0.10;
      rules.push('RULE_BREAKOUT_V1d: unstable gamma regime amplifies directional moves');
    }
    // Pinning regime argues against breakout
    if (gammaInterp.regime === 'pinning') {
      score -= 0.15;
      rules.push('RULE_BREAKOUT_V2c: pinning regime — breakout likely to be absorbed');
    }
  }

  // SMC structural confirmation
  if (smc) {
    const bosDir = smc.lastBOS?.dir;
    const chopDir = smc.lastCHOCH?.dir;
    const dirLabel = dir === 'up' ? 'bullish' : 'bearish';
    if (bosDir === dirLabel && smc.displacementFreshness >= T.DISPLACEMENT_FRESH_MIN) {
      score += 0.15;
      rules.push('RULE_BREAKOUT_V3: fresh BOS in breakout direction — structural confirmation');
    }
    if (chopDir === dirLabel) {
      // CHOCH in the breakout direction means structure shifted — supports break
      score += 0.10;
      rules.push('RULE_BREAKOUT_V3b: CHOCH confirms breakout direction');
    }
    if (smc.sweepAndReverse && dir === 'up') {
      // Sweep-and-reverse after a low sweep = bullish, supports up breakout
      score += 0.10;
      rules.push('RULE_BREAKOUT_V3c: sweep-and-reverse supports upside breakout');
    }
  }

  // RVOL gate
  const rvol = regime?.rvol ?? 1.0;
  if (rvol < T.RVOL_WEAK_BREAKOUT) {
    score -= 0.15;
    rules.push(`RULE_BREAKOUT_V4: weak RVOL (${rvol.toFixed(2)}x) — low institutional participation`);
  } else if (rvol >= T.RVOL_STRONG_MOVE) {
    score += 0.10;
    rules.push(`RULE_BREAKOUT_V4b: strong RVOL (${rvol.toFixed(2)}x) — institutional participation`);
  }

  return { score: _round2(_clamp(score, 0, 1)), rules };
}

// ── Step 2: Score breakout trap risk ─────────────────────────────────────────
//
// RULE_BULL_TRAP_1: Validated resistance wall + pinning + rejection behavior
//   → bull trap HIGH risk
//
// RULE_BULL_TRAP_2: SMC false breakout (closed back below level) + weak volume
//   → bull trap confirmed by candle engine + gamma context
//
// RULE_BEAR_TRAP_1: Validated support wall + sweep-and-reclaim + absorption
//   → bear trap HIGH risk

function _scoreBullTrapRisk(inputs: TrapInterpreterInputs): { score: number; rules: string[] } {
  const { traps, gammaInterp, liqOutput, smc, regime } = inputs;
  const rules: string[] = [];
  let score = 0;

  // Candle engine already detected a bull trap
  const candleBullTraps = traps.filter(t => t.type === 'Bull Trap Risk' || t.type === 'Prior Swing High Sweep');
  if (candleBullTraps.length > 0) {
    score += candleBullTraps.some(t => t.severity === 'High') ? 0.35 : 0.20;
    rules.push(`RULE_BULL_TRAP_1: candle engine: ${candleBullTraps.map(t => t.type).join(', ')}`);
  }

  if (gammaInterp && gammaInterp.confidence >= T.MIN_REGIME_CONF_FOR_UPGRADE) {
    // Pinning + validated resistance = wall likely to hold → upside breakout is a trap
    if (gammaInterp.regime === 'pinning' && liqOutput?.signals.validatedWallResistance) {
      score += 0.25;
      rules.push('RULE_BULL_TRAP_2: pinning regime + validated resistance wall — upside trapped');
    }
    // Compression fragile: compression releasing into weak structure
    if (liqOutput?.signals.compressionFragile) {
      score += 0.15;
      rules.push('RULE_BULL_TRAP_3: fragile compression — release likely to fail');
    }
    // Reversal bias from gamma: spot extended above wall, reversion pressure
    if (gammaInterp.signals.reversalBias && gammaInterp.directionalBias === 'bearish') {
      score += 0.20;
      rules.push('RULE_BULL_TRAP_4: gamma reversalBias bearish — upside extension likely trapped');
    }
    // Rejection behavior at dominant level
    if (liqOutput?.interactionSummary.dominantBehavior === 'rejection') {
      score += 0.15;
      rules.push('RULE_BULL_TRAP_5: rejection behavior at dominant gamma level');
    }
  }

  // SMC: failed sweep of swing high
  if (smc?.recentSweep?.type === 'sweep_high' && smc.sweepAndReverse) {
    score += 0.20;
    rules.push('RULE_BULL_TRAP_6: SMC sweep_high + sweepAndReverse — bull trap pattern');
  }

  // Weak RVOL on upside move amplifies trap risk
  if ((regime?.rvol ?? 1.0) < T.RVOL_WEAK_BREAKOUT) {
    score += 0.10;
    rules.push('RULE_BULL_TRAP_7: weak RVOL amplifies bull trap risk');
  }

  return { score: _round2(_clamp(score, 0, 1)), rules };
}

function _scoreBearTrapRisk(inputs: TrapInterpreterInputs): { score: number; rules: string[] } {
  const { traps, gammaInterp, liqOutput, smc } = inputs;
  const rules: string[] = [];
  let score = 0;

  // Candle engine bear trap signals
  const candleBearTraps = traps.filter(t =>
    t.type === 'Bear Trap Risk' || t.type === 'Sweep & Reclaim' || t.type === 'Prior Swing Low Sweep'
  );
  if (candleBearTraps.length > 0) {
    score += candleBearTraps.some(t => t.severity === 'High') ? 0.35 : 0.20;
    rules.push(`RULE_BEAR_TRAP_1: candle engine: ${candleBearTraps.map(t => t.type).join(', ')}`);
  }

  if (gammaInterp && gammaInterp.confidence >= T.MIN_REGIME_CONF_FOR_UPGRADE) {
    // Validated support + absorption = downside breakdown is a trap
    if (gammaInterp.regime === 'pinning' && liqOutput?.signals.validatedWallSupport) {
      score += 0.25;
      rules.push('RULE_BEAR_TRAP_2: pinning regime + validated support wall — downside trapped');
    }
    // Reversal bias from gamma: spot extended below wall, reversion pressure
    if (gammaInterp.signals.reversalBias && gammaInterp.directionalBias === 'bullish') {
      score += 0.20;
      rules.push('RULE_BEAR_TRAP_3: gamma reversalBias bullish — downside extension likely trapped');
    }
    // Absorption behavior at dominant level
    if (liqOutput?.interactionSummary.dominantBehavior === 'absorption') {
      score += 0.15;
      rules.push('RULE_BEAR_TRAP_4: absorption at dominant gamma level — downside likely defended');
    }
  }

  // SMC sweep of swing low + reclaim
  if (smc?.recentSweep?.type === 'sweep_low' && smc.sweepAndReverse) {
    score += 0.20;
    rules.push('RULE_BEAR_TRAP_5: SMC sweep_low + sweepAndReverse — bear trap pattern');
  }

  return { score: _round2(_clamp(score, 0, 1)), rules };
}

// ── Step 3: Score continuation quality ───────────────────────────────────────
//
// RULE_CONT_1: Negative gamma + confirmed breakout + fresh BOS + strong RVOL
//   → continuation quality HIGH
//
// RULE_CONT_2: Pinning regime + validated walls in continuation direction
//   → continuation quality LOW (structural barriers)
//
// RULE_CONT_3: Momentum stall (Failed Continuation candle signal) + any pinning
//   → continuation quality LOW

function _scoreContinuationQuality(inputs: TrapInterpreterInputs): { score: number; rules: string[] } {
  const { traps, gammaInterp, liqOutput, smc, regime } = inputs;
  const rules: string[] = [];
  let score = 0.50;

  // Candle engine failed continuation
  const failedCont = traps.filter(t => t.type === 'Failed Continuation Risk');
  if (failedCont.length > 0) {
    score -= 0.25;
    rules.push('RULE_CONT_3: candle engine Failed Continuation Risk detected');
  }

  if (gammaInterp && gammaInterp.confidence >= T.MIN_REGIME_CONF_FOR_UPGRADE) {
    if (gammaInterp.regime === 'unstable' && liqOutput?.interactionSummary.breakoutValidation === 'confirmed') {
      score += 0.25;
      rules.push('RULE_CONT_1: unstable gamma + confirmed breakout = high continuation quality');
    }
    if (gammaInterp.regime === 'pinning') {
      score -= 0.20;
      rules.push('RULE_CONT_2: pinning regime — continuation is structurally resisted');
    }
    if (liqOutput?.signals.compressionStable) {
      score -= 0.10;
      rules.push('RULE_CONT_2b: stable compression — contained movement, not expansion');
    }
    if (gammaInterp.signals.expansionRisk) {
      score += 0.15;
      rules.push('RULE_CONT_1b: gamma expansionRisk — dealers amplify moves, continuation supported');
    }
  }

  // SMC displacement confirmation
  if (smc != null && (smc.displacementFreshness ?? 0) >= T.DISPLACEMENT_FRESH_MIN && smc.displacementBreaksStructure) {
    score += 0.15;
    rules.push('RULE_CONT_1c: fresh SMC displacement breaks structure — continuation momentum');
  }

  // RVOL
  const rvol = regime?.rvol ?? 1.0;
  if (rvol >= T.RVOL_STRONG_MOVE) {
    score += 0.15;
    rules.push(`RULE_CONT_1d: RVOL ${rvol.toFixed(2)}x — strong institutional participation`);
  } else if (rvol < T.RVOL_WEAK_BREAKOUT) {
    score -= 0.15;
    rules.push(`RULE_CONT_2c: RVOL ${rvol.toFixed(2)}x — weak participation undermines continuation`);
  }

  return { score: _round2(_clamp(score, 0, 1)), rules };
}

// ── Step 4: Score reversal quality ───────────────────────────────────────────
//
// RULE_REV_1: SMC CHOCH + gamma reversalBias + validated wall + absorption
//   → reversal quality HIGH
//
// RULE_REV_2: Reversal attempt but gamma regime is unstable + expansionRisk
//   → false reversal risk (continuation still favored)

function _scoreReversalQuality(inputs: TrapInterpreterInputs): { score: number; rules: string[] } {
  const { gammaInterp, liqOutput, smc } = inputs;
  const rules: string[] = [];
  let score = 0.40; // default slightly below medium — reversals require confirmation

  if (gammaInterp && gammaInterp.confidence >= T.MIN_REGIME_CONF_FOR_UPGRADE) {
    if (gammaInterp.signals.reversalBias) {
      score += 0.20;
      rules.push('RULE_REV_1a: gamma reversalBias active — reversion pressure present');
    }
    if (gammaInterp.regime === 'unstable' && gammaInterp.signals.expansionRisk) {
      score -= 0.25;
      rules.push('RULE_REV_2: unstable gamma + expansionRisk — reversal attempt likely false');
    }
    if (gammaInterp.regime === 'pinning' && liqOutput?.signals.compressionStable) {
      score += 0.15;
      rules.push('RULE_REV_1b: pinning + stable compression — level defense supports reversal');
    }
    if (liqOutput?.interactionSummary.dominantBehavior === 'absorption') {
      score += 0.15;
      rules.push('RULE_REV_1c: absorption behavior — buying/selling defended, reversal likely');
    }
    if (liqOutput?.interactionSummary.dominantBehavior === 'rejection') {
      score -= 0.15;
      rules.push('RULE_REV_2b: rejection behavior suggests failed reversal attempt');
    }
  }

  // SMC CHOCH = structural reversal signal (strong)
  if (smc?.lastCHOCH) {
    score += 0.20;
    rules.push(`RULE_REV_1d: SMC CHOCH ${smc.lastCHOCH.dir} @ ${smc.lastCHOCH.price.toFixed(2)} — structural reversal`);
  }

  // Sweep-and-reverse = confirmed reversal with stop-run
  if (smc?.sweepAndReverse) {
    score += 0.15;
    rules.push('RULE_REV_1e: SMC sweepAndReverse — stop-run then reclaim confirms reversal');
  }

  return { score: _round2(_clamp(score, 0, 1)), rules };
}

// ── Step 5: Score pin release quality ────────────────────────────────────────
//
// RULE_PIN_1: Compression fragile + near flip zone + breakout suspect
//   → pin release trap (release will fail)
//
// RULE_PIN_2: Compression stable + confirmed breakout + fresh displacement
//   → healthy release (not a trap)

function _scorePinReleaseQuality(inputs: TrapInterpreterInputs): { score: number; rules: string[] } {
  const { gammaInterp, liqOutput, smc } = inputs;
  const rules: string[] = [];
  let score = 0.50;

  if (!gammaInterp?.signals.compression && !liqOutput?.signals.compressionStable && !liqOutput?.signals.compressionFragile) {
    // Not in a compression regime — pin release is not applicable
    return { score: 0.50, rules: ['RULE_PIN_N/A: no compression regime active'] };
  }

  if (gammaInterp && gammaInterp.confidence >= T.MIN_REGIME_CONF_FOR_UPGRADE) {
    if (liqOutput?.signals.compressionFragile) {
      score -= 0.25;
      rules.push('RULE_PIN_1a: compression fragile — release quality poor');
    }
    if (liqOutput?.signals.compressionStable) {
      score += 0.15;
      rules.push('RULE_PIN_2a: compression stable — release quality better');
    }
    if (liqOutput?.interactionSummary.breakoutValidation === 'confirmed') {
      score += 0.20;
      rules.push('RULE_PIN_2b: breakout confirmed — release has structural support');
    }
    if (liqOutput?.interactionSummary.breakoutValidation === 'rejected') {
      score -= 0.20;
      rules.push('RULE_PIN_1b: breakout rejected — release is failing');
    }
    // Flip zone proximity during release = increased trap risk
    const flipProx = gammaInterp.flipZone.proximity;
    if (flipProx !== null && flipProx <= T.FLIP_ZONE_TRAP_PCT) {
      score -= 0.20;
      rules.push(`RULE_PIN_1c: release near flip zone (${(flipProx * 100).toFixed(1)}%) — whipsaw risk elevated`);
    }
  }

  if (smc != null && (smc.displacementFreshness ?? 0) >= T.DISPLACEMENT_FRESH_MIN && smc.displacementBreaksStructure) {
    score += 0.15;
    rules.push('RULE_PIN_2c: fresh SMC displacement on release — structurally clean');
  }

  return { score: _round2(_clamp(score, 0, 1)), rules };
}

// ── Step 6: Score flip zone trap / whipsaw risk ───────────────────────────────
//
// RULE_FLIP_1: Spot within FLIP_ZONE_TRAP_PCT of flip midpoint + unstable regime
//   → flip_zone_trap HIGH
//
// RULE_FLIP_2: Any existing trap signal near flip zone
//   → compound trap risk (convergence of candle trap + gamma instability)

function _scoreFlipZoneTrap(inputs: TrapInterpreterInputs): { score: number; rules: string[] } {
  const { spot, traps, gammaInterp } = inputs;
  const rules: string[] = [];
  let score = 0;

  if (!gammaInterp) return { score: 0, rules: ['RULE_FLIP_N/A: no gamma interpretation'] };

  const flipProx = gammaInterp.flipZone.proximity;
  if (flipProx === null) return { score: 0, rules: ['RULE_FLIP_N/A: flip zone not estimated'] };

  if (flipProx <= T.FLIP_ZONE_TRAP_PCT) {
    score += 0.40;
    rules.push(`RULE_FLIP_1: spot within ${(T.FLIP_ZONE_TRAP_PCT * 100).toFixed(1)}% of flip zone midpoint — directional uncertainty high`);

    if (gammaInterp.regime === 'unstable') {
      score += 0.25;
      rules.push('RULE_FLIP_1b: unstable gamma + near flip zone — whipsaw risk maximum');
    }
    if (gammaInterp.flipZone.spotPosition === 'inside_flip') {
      score += 0.20;
      rules.push('RULE_FLIP_1c: spot INSIDE flip zone — gamma balance point; extreme directional unreliability');
    }

    // Any existing trap signal near flip zone compounds risk
    const { low, high } = gammaInterp.flipZone;
    const flipMid = (low !== null && high !== null) ? (low + high) / 2 : low ?? high ?? spot;
    const nearbyTraps = traps.filter(t =>
      t.level !== null && Math.abs((t.level - flipMid) / spot) <= 0.025
    );
    if (nearbyTraps.length > 0) {
      score += 0.15;
      rules.push(`RULE_FLIP_2: ${nearbyTraps.length} candle trap(s) near flip zone — compound risk`);
    }
  }

  return { score: _round2(_clamp(score, 0, 1)), rules };
}

// ── Step 7: trapSide — strictly derived from final classification ─────────────
//
// trapSide must always be consistent with trapClassification.
// This is the single source of truth — never derived independently.
//
// For direction-dependent classifications (failed_continuation, false_reversal,
// pin_release), the directional bias from gamma is used as a tiebreaker.

function _mapTrapSide(
  classification: TrapClassification,
  gammaInterp:    GammaV2Interpretation | null | undefined,
  bullScore:      number,
  bearScore:      number,
): TrapSideAssessment {
  switch (classification) {
    case 'breakout_bull_trap':
      return 'longs_at_risk';

    case 'breakdown_bear_trap':
      return 'shorts_at_risk';

    case 'flip_zone_trap':
      return 'both';

    case 'failed_continuation_trap':
    case 'false_reversal_trap': {
      // Derive from gamma directional bias; fall back to dominant score
      const bias = gammaInterp?.directionalBias;
      if (bias === 'bullish')  return 'longs_at_risk';  // bullish move failing → longs at risk
      if (bias === 'bearish')  return 'shorts_at_risk';
      // Score-based fallback
      if (bullScore > bearScore) return 'longs_at_risk';
      if (bearScore > bullScore) return 'shorts_at_risk';
      return 'both';
    }

    case 'pin_release_trap': {
      // Pin release direction comes from gamma bias
      const bias = gammaInterp?.directionalBias;
      if (bias === 'bullish')  return 'longs_at_risk';
      if (bias === 'bearish')  return 'shorts_at_risk';
      return 'both';   // symmetric when direction unknown
    }

    case 'none':
    default:
      return 'none';
  }
}

// ── Step 8: Resolve final trap classification ─────────────────────────────────
//
// STRICT PRIORITY ORDER (deterministic — evaluated top-to-bottom):
//
//   1. flip_zone_trap         — dominates when whipsawRisk is high (flipScore ≥ HIGH_TRAP_RISK_MIN)
//   2. breakout_bull_trap     — requires BOTH bullTrapScore ≥ HIGH AND breakoutValidity 'low'
//   3. breakdown_bear_trap    — requires BOTH bearTrapScore ≥ HIGH AND breakdownValidity 'low'
//   4. breakout_bull_trap     — relaxed: bullTrapScore ≥ MED and strictly above bear
//   5. breakdown_bear_trap    — relaxed: bearTrapScore ≥ MED and strictly above bull
//   6. pin_release_trap       — compression active + release quality low
//   7. false_reversal_trap    — reversalQuality 'low' + continuationQuality 'high'
//   8. failed_continuation_trap — continuationQuality 'low'
//   9. none
//
// Signal-to-classification coupling:
//   breakout_bull_trap     ← breakoutValidity='low' AND trapRisk='medium'|'high'
//   breakdown_bear_trap    ← breakdownValidity='low' AND trapRisk='medium'|'high'
//   failed_continuation    ← continuationQuality='low'
//   false_reversal         ← reversalQuality='low' AND continuationQuality='high'
//   pin_release_trap       ← releaseQuality='low' (when compression active)
//
// Flip-zone dominance:
//   When flipScore ≥ HIGH_TRAP_RISK_MIN the flip_zone_trap overrides ALL other
//   classifications except when an extremely confirmed breakout exists.
//   Exception: breakoutValidity='high' suppresses the flip_zone_trap override
//   only when the flip score is exactly at the boundary (≥ HIGH but < 0.80).

function _resolveClassification(
  bullTrapScore:      number,
  bearTrapScore:      number,
  flipScore:          number,
  pinReleaseScore:    number,
  contScore:          number,
  revScore:           number,
  breakoutUpScore:    number,
  breakoutDownScore:  number,
  highVol:            boolean,   // true when vol regime is high_vol or extreme_vol
): { classification: TrapClassification; appliedRule: string } {

  // Derive signal quality labels (same logic as main function)
  const breakoutValidity   = _scoreToQuality(Math.max(breakoutUpScore, breakoutDownScore));
  const breakdownValidity  = _scoreToQuality(Math.max(breakoutDownScore, breakoutUpScore));
  const continuationQuality = _scoreToQuality(contScore);
  const reversalQuality    = _scoreToQuality(revScore);
  const releaseQuality     = _scoreToQuality(pinReleaseScore);
  const overallTrapScore   = Math.max(bullTrapScore, bearTrapScore, flipScore * 0.80, (1 - contScore) * 0.60);
  const trapRisk           = _scoreToRisk(overallTrapScore);

  // ── PRIORITY 0: Flip-zone + high/extreme vol override ────────────────────────
  // When VIX is elevated AND flip proximity is meaningful, the structural
  // instability compounds — flip_zone_trap dominates unconditionally.
  // This is the one place volatility CAN upgrade a classification directly,
  // because flip zone + vol stress represents the highest-uncertainty state.
  if (flipScore >= T.MED_TRAP_RISK_MIN && highVol) {
    return { classification: 'flip_zone_trap', appliedRule: 'PRIORITY_0_FLIP_PLUS_HIGH_VOL' };
  }

  // ── PRIORITY 1: Flip-zone trap (dominates) ─────────────────────────────────
  // Exception: a very high-confidence confirmed breakout (breakoutValidity='high')
  // may suppress the override when flip score is only marginally above the threshold.
  const flipDominates = flipScore >= T.HIGH_TRAP_RISK_MIN &&
    !(breakoutValidity === 'high' && flipScore < 0.80);
  if (flipDominates) {
    return { classification: 'flip_zone_trap', appliedRule: 'PRIORITY_1_FLIP_ZONE_DOMINANT' };
  }

  // ── PRIORITY 2+3: Breakout traps — require signal-level gate ──────────────
  // Strict path: HIGH trap score + breakoutValidity='low' (confirms the break is suspect)
  if (bullTrapScore >= T.HIGH_TRAP_RISK_MIN && breakoutValidity === 'low' && bullTrapScore >= bearTrapScore) {
    return { classification: 'breakout_bull_trap', appliedRule: 'PRIORITY_2_BULL_TRAP_HIGH_GATED' };
  }
  if (bearTrapScore >= T.HIGH_TRAP_RISK_MIN && breakdownValidity === 'low' && bearTrapScore > bullTrapScore) {
    return { classification: 'breakdown_bear_trap', appliedRule: 'PRIORITY_3_BEAR_TRAP_HIGH_GATED' };
  }

  // High trap score without the validity gate (e.g. strong candle pattern alone)
  if (bullTrapScore >= T.HIGH_TRAP_RISK_MIN && trapRisk !== 'low' && bullTrapScore >= bearTrapScore) {
    return { classification: 'breakout_bull_trap', appliedRule: 'PRIORITY_2_BULL_TRAP_HIGH' };
  }
  if (bearTrapScore >= T.HIGH_TRAP_RISK_MIN && trapRisk !== 'low' && bearTrapScore > bullTrapScore) {
    return { classification: 'breakdown_bear_trap', appliedRule: 'PRIORITY_3_BEAR_TRAP_HIGH' };
  }

  // Relaxed path: MED score — requires trapRisk = medium|high to classify
  if (bullTrapScore >= T.MED_TRAP_RISK_MIN && trapRisk !== 'low' && bullTrapScore > bearTrapScore) {
    return { classification: 'breakout_bull_trap', appliedRule: 'PRIORITY_4_BULL_TRAP_MED' };
  }
  if (bearTrapScore >= T.MED_TRAP_RISK_MIN && trapRisk !== 'low' && bearTrapScore >= bullTrapScore) {
    return { classification: 'breakdown_bear_trap', appliedRule: 'PRIORITY_4_BEAR_TRAP_MED' };
  }

  // ── PRIORITY 5: Pin release trap ───────────────────────────────────────────
  // Requires: releaseQuality='low' (not just a raw score threshold)
  if (releaseQuality === 'low') {
    return { classification: 'pin_release_trap', appliedRule: 'PRIORITY_5_PIN_RELEASE_LOW_QUALITY' };
  }

  // ── PRIORITY 6: False reversal ─────────────────────────────────────────────
  // Requires: reversalQuality='low' + continuationQuality='high'
  if (reversalQuality === 'low' && continuationQuality === 'high') {
    return { classification: 'false_reversal_trap', appliedRule: 'PRIORITY_6_FALSE_REVERSAL_GATED' };
  }

  // ── PRIORITY 7: Failed continuation ───────────────────────────────────────
  // Requires: continuationQuality='low'
  if (continuationQuality === 'low') {
    return { classification: 'failed_continuation_trap', appliedRule: 'PRIORITY_7_FAILED_CONTINUATION_GATED' };
  }

  return { classification: 'none', appliedRule: 'NO_TRAP_THRESHOLD_MET' };
}

// ── Step 9: Confidence model — conservative, min-based ───────────────────────
//
// baseConfidence = min(available sources) — most conservative available signal
//   When sources are absent, they contribute 0.50 (unknown, not zero)
//
// Penalty multipliers (applied multiplicatively):
//   conflictPenalty:   when bull and bear scores are both in medium range → ambiguous
//   fallbackPenalty:   when provider fallback is active
//   singleSourcePenalty: when only one signal source is available
//
// This model cannot inflate confidence: adding more sources either helps via
// their actual quality or is neutral when absent.

function _combineConfidence(
  gammaConf:     number,
  liqConf:       number,
  smcConf:       number,
  triggerCount:  number,
  bullScore:     number,
  bearScore:     number,
  fallbackActive: boolean,
): number {
  // Absent sources contribute a neutral 0.50 (unknown) rather than pulling to 0
  const sources = [
    gammaConf  > 0 ? gammaConf : 0.50,
    liqConf    > 0 ? liqConf   : 0.50,
    smcConf,
  ];

  // Base = minimum of available sources (most conservative)
  const base = Math.min(...sources);

  // Conflict penalty: both bull and bear moderate → directionally ambiguous
  const bothMed = bullScore >= T.MED_TRAP_RISK_MIN && bearScore >= T.MED_TRAP_RISK_MIN;
  const conflictPenalty = bothMed ? 0.85 : 1.00;

  // Fallback penalty
  const fallbackPenalty = fallbackActive ? 0.90 : 1.00;

  // Single-source penalty: only candle traps available (no gamma/liq/smc)
  const singleSourcePenalty = triggerCount <= 1 ? 0.80 : 1.00;

  return _round2(_clamp(base * conflictPenalty * fallbackPenalty * singleSourcePenalty, 0, 1));
}

// ── Step 10: Build reasoning — stable section order ──────────────────────────

function _buildReasoning(
  inputs:             TrapInterpreterInputs,
  classification:     TrapClassification,
  appliedRule:        string,
  bullRules:          string[],
  bearRules:          string[],
  contRules:          string[],
  revRules:           string[],
  pinRules:           string[],
  flipRules:          string[],
  confidence:         number,
): string[] {
  const { gammaInterp, liqOutput, smc, regime, traps } = inputs;
  const r: string[] = [];

  // ── Section 1: Regime / gamma context ────────────────────────────────────
  if (gammaInterp) {
    r.push(
      `Gamma: ${gammaInterp.regime} regime (regimeConf=${(gammaInterp.regimeConfidence * 100).toFixed(0)}%) ` +
      `| bias=${gammaInterp.directionalBias} | posture score=${gammaInterp.directionalScore}`
    );
  } else {
    r.push('Gamma: unavailable — classification based on candle/SMC/regime data only');
  }
  if (regime) {
    r.push(
      `Regime: ${regime.dominant} (conf=${regime.confidence}%) | phase=${regime.phase} ` +
      `| RVOL=${regime.rvol.toFixed(2)}x | VWAP dist=${regime.vwapDist.toFixed(2)}%`
    );
  }

  // ── Section 2: Key structure / wall / flip zone ───────────────────────────
  if (gammaInterp?.structure.nearestWallAbove !== null || gammaInterp?.structure.nearestWallBelow !== null) {
    r.push(
      `Structure: call wall=${gammaInterp?.structure.nearestWallAbove?.toFixed(2) ?? '—'} ` +
      `put wall=${gammaInterp?.structure.nearestWallBelow?.toFixed(2) ?? '—'} ` +
      `magnet=${gammaInterp?.structure.keyMagnetStrike?.toFixed(2) ?? '—'}`
    );
  }
  if (gammaInterp?.flipZone.low !== null || gammaInterp?.flipZone.high !== null) {
    const prox = gammaInterp?.flipZone.proximity;
    r.push(
      `Flip zone: ${gammaInterp?.flipZone.low?.toFixed(2) ?? '?'}–${gammaInterp?.flipZone.high?.toFixed(2) ?? '?'} ` +
      `(${prox !== null && prox !== undefined ? (prox * 100).toFixed(1) + '% from midpoint' : 'proximity unknown'}) ` +
      `— spot ${gammaInterp?.flipZone.spotPosition?.replace(/_/g, ' ') ?? 'unknown'}`
    );
  }
  if (smc?.lastBOS) {
    r.push(`SMC: last BOS ${smc.lastBOS.dir} @ ${smc.lastBOS.price.toFixed(2)} (${smc.lastBOS.strength})`);
  }
  if (smc?.lastCHOCH) {
    r.push(`SMC: CHOCH ${smc.lastCHOCH.dir} @ ${smc.lastCHOCH.price.toFixed(2)} — structural shift`);
  }

  // ── Section 3: Liquidity / validation behavior ────────────────────────────
  if (liqOutput) {
    r.push(
      `Liquidity: behavior=${liqOutput.interactionSummary.dominantBehavior} ` +
      `compression=${liqOutput.interactionSummary.compressionQuality} ` +
      `breakout=${liqOutput.interactionSummary.breakoutValidation} ` +
      `trapRisk=${liqOutput.interactionSummary.trapRisk}`
    );
    const fakeWalls = liqOutput.wallValidation.filter(w => w.status === 'fake_risk');
    if (fakeWalls.length > 0) {
      r.push(`Wall alert: ${fakeWalls.length} fake_risk wall(s) at ${fakeWalls.map(w => w.strike.toFixed(2)).join(', ')}`);
    }
  }
  if (traps.length > 0) {
    const top = traps[0];
    r.push(`Candle traps: ${traps.length} active — top: ${top.type} (${top.severity}) @ ${top.level?.toFixed(2) ?? 'N/A'}`);
  }

  // ── Section 4: Resolved trap interpretation ───────────────────────────────
  r.push(`Trap classification: ${classification} [${appliedRule}]`);

  // Surface the most relevant rule set for the final classification
  const activeRules =
    classification === 'breakout_bull_trap'      ? bullRules  :
    classification === 'breakdown_bear_trap'     ? bearRules  :
    classification === 'failed_continuation_trap'? contRules  :
    classification === 'false_reversal_trap'     ? revRules   :
    classification === 'pin_release_trap'        ? pinRules   :
    classification === 'flip_zone_trap'          ? flipRules  :
    [];
  for (const rule of activeRules.slice(0, 3)) {
    r.push(`  ↳ ${rule}`);
  }

  // ── Section 5: Confidence / caution note ──────────────────────────────────
  if (confidence < 0.40) {
    r.push(`Low confidence (${(confidence * 100).toFixed(0)}%) — insufficient gamma/liquidity data; classification is tentative`);
  } else {
    r.push(`Confidence: ${(confidence * 100).toFixed(0)}%`);
  }

  return r;
}

// ── Step 11: Trap strength ───────────────────────────────────────────────────
//
// trapStrength reflects signal intensity — how compelling the trap evidence is.
//
//   strong:   trapRisk='high' + ≥2 aligned signals + confidence ≥ 0.55 + proximity
//   moderate: trapRisk='medium' OR (high + 1 signal) OR (moderate confidence)
//   weak:     trapRisk='low' OR confidence < 0.30 OR single-source

function _deriveTrapStrength(
  classification:  TrapClassification,
  trapRisk:        RiskLevel,
  confidence:      number,
  triggerCount:    number,
  flipScore:       number,
  bullScore:       number,
  bearScore:       number,
  isHighVol:       boolean,
): 'weak' | 'moderate' | 'strong' {
  if (classification === 'none') return 'weak';

  // Flip zone + high/extreme vol → force 'strong' (environment is structurally unstable)
  if (classification === 'flip_zone_trap' && isHighVol) return 'strong';

  // Count aligned signals (each scoring domain that agrees with the classification)
  let alignedSignals = 0;
  if (trapRisk === 'high')           alignedSignals++;
  if (triggerCount >= 3)             alignedSignals++;
  if (flipScore >= T.MED_TRAP_RISK_MIN && classification === 'flip_zone_trap') alignedSignals++;
  if (bullScore >= T.HIGH_TRAP_RISK_MIN && classification === 'breakout_bull_trap') alignedSignals++;
  if (bearScore >= T.HIGH_TRAP_RISK_MIN && classification === 'breakdown_bear_trap') alignedSignals++;

  if (trapRisk === 'high' && alignedSignals >= 2 && confidence >= 0.55) return 'strong';
  if (trapRisk === 'high' || (trapRisk === 'medium' && confidence >= 0.45)) return 'moderate';
  return 'weak';
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Interpret trap conditions from all available context sources.
 *
 * @param inputs — all available context (see TrapInterpreterInputs)
 * @returns TrapEngineOutput — deterministic, explainable, replay-safe
 */
export function interpretTraps(inputs: TrapInterpreterInputs): TrapEngineOutput {
  const { symbol, traps, gammaInterp, liqOutput, smc, regime, volContext } = inputs;

  // Source confidence derivation
  const gammaConf = gammaInterp?.confidence ?? 0;
  const liqConf   = liqOutput?.confidence   ?? 0;
  const smcConf   = smc?.confidenceScore    ?? 0.5;

  // Score all trap dimensions
  const { score: bullScore,  rules: bullRules  } = _scoreBullTrapRisk(inputs);
  const { score: bearScore,  rules: bearRules  } = _scoreBearTrapRisk(inputs);
  const { score: contScore,  rules: contRules  } = _scoreContinuationQuality(inputs);
  const { score: revScore,   rules: revRules   } = _scoreReversalQuality(inputs);
  const { score: pinScore,   rules: pinRules   } = _scorePinReleaseQuality(inputs);
  const { score: flipScore,  rules: flipRules  } = _scoreFlipZoneTrap(inputs);

  // Breakout validity (both directions)
  const { score: upBreakout  } = _scoreBreakoutValidity(inputs, 'up');
  const { score: downBreakout } = _scoreBreakoutValidity(inputs, 'down');

  // Invert to get trap risk: high breakout validity = low trap risk for that direction
  const breakoutUpTrapRisk   = 1 - upBreakout;
  const breakoutDownTrapRisk = 1 - downBreakout;

  // Combine directional trap risk with pattern-specific scores
  const finalBullScore = _round2(_clamp(bullScore * 0.60 + breakoutUpTrapRisk   * 0.40, 0, 1));
  const finalBearScore = _round2(_clamp(bearScore * 0.60 + breakoutDownTrapRisk * 0.40, 0, 1));

  // Trigger source count (for confidence model)
  const triggerCount = [
    gammaInterp !== null && gammaInterp !== undefined ? 1 : 0,
    liqOutput   !== null && liqOutput   !== undefined ? 1 : 0,
    smc         !== null && smc         !== undefined ? 1 : 0,
    traps.length > 0 ? 1 : 0,
  ].reduce((s, v) => s + v, 0);

  // Aggregate trap risk score — apply vol regime modifier
  const rawTrapScore = _round2(
    Math.max(finalBullScore, finalBearScore, flipScore * 0.80, (1 - contScore) * 0.60)
  );
  const overallTrapScore = applyVolTrapModifier(rawTrapScore, volContext);

  // Derive signal quality labels — these gate classification (signals first)
  const breakoutValidity    = _scoreToQuality(Math.max(upBreakout, downBreakout));
  const breakdownValidity   = _scoreToQuality(Math.max(downBreakout, upBreakout));
  const continuationQuality = _scoreToQuality(contScore);
  const reversalQuality     = _scoreToQuality(revScore);
  const releaseQuality      = _scoreToQuality(pinScore);
  const whipsawRisk         = _scoreToRisk(flipScore);
  const trapRisk            = _scoreToRisk(overallTrapScore);

  // Conservative confidence (min-based with penalties)
  const baseConfidence = _combineConfidence(
    gammaConf, liqConf, smcConf, triggerCount,
    finalBullScore, finalBearScore,
    liqOutput?.meta.fallbackActive ?? false,
  );
  // Apply volatility context modifier — high/extreme vol reduces structural confidence
  const confidence = applyVolConfidenceModifier(baseConfidence, volContext);

  // Resolve classification — strictly gated on signal quality labels
  const isHighVol = volContext?.regime === 'high_vol' || volContext?.regime === 'extreme_vol';
  const { classification, appliedRule } = _resolveClassification(
    finalBullScore, finalBearScore, flipScore, pinScore, contScore, revScore,
    upBreakout, downBreakout, isHighVol,
  );

  // trapSide — always derived from classification (never independent)
  const trapSide = _mapTrapSide(classification, gammaInterp, finalBullScore, finalBearScore);

  // Trap strength — intensity of the trap evidence
  const trapStrength = _deriveTrapStrength(
    classification, trapRisk, confidence, triggerCount,
    flipScore, finalBullScore, finalBearScore, isHighVol,
  );

  // Structure fields
  const structure = {
    keyLevel:         gammaInterp?.structure.keyMagnetStrike ?? null,
    nearestWallAbove: gammaInterp?.structure.nearestWallAbove ?? null,
    nearestWallBelow: gammaInterp?.structure.nearestWallBelow ?? null,
    flipZoneLow:      gammaInterp?.flipZone.low  ?? null,
    flipZoneHigh:     gammaInterp?.flipZone.high ?? null,
  };

  // Regime context
  const regimeContext = {
    regime:          gammaInterp?.regime ?? (regime?.dominant ?? 'unknown'),
    directionalBias: gammaInterp?.directionalBias ?? 'neutral',
    gammaPosture:    gammaInterp
      ? (gammaInterp.pinScore > gammaInterp.expansionScore ? 'positive_leaning' : 'negative_leaning')
      : 'unknown',
  };

  // Trigger sources
  const triggerSources: string[] = [];
  if (gammaInterp) triggerSources.push('gamma_v2');
  if (liqOutput)   triggerSources.push('gamma_liquidity');
  if (smc)         triggerSources.push('smc');
  if (regime)      triggerSources.push('regime');
  if (traps.length > 0) triggerSources.push(`candle_traps(${traps.length})`);
  if (volContext)  triggerSources.push(`vol_context(${volContext.regime})`);

  // Reasoning
  const reasoning = _buildReasoning(
    inputs, classification, appliedRule,
    bullRules, bearRules, contRules, revRules, pinRules, flipRules, confidence
  );

  return {
    symbol,
    asOf:               new Date().toISOString(),
    trapClassification: classification,
    trapSide,
    appliedRule,
    trapStrength,
    signals: {
      breakoutValidity,
      breakdownValidity,
      continuationQuality,
      reversalQuality,
      releaseQuality,
      whipsawRisk,
      trapRisk,
    },
    structure,
    regimeContext,
    confidence,
    regimeConfidence: (() => {
      const raw = gammaInterp?.regimeConfidence ?? null;
      if (raw === null) return null;
      return applyVolRegimeConfModifier(raw, volContext);
    })(),
    reasoning,
    volatilityContext: volContext
      ? { regime: volContext.regime, impact: regimeToImpact(volContext.regime) }
      : null,

    meta: {
      fallbackActive:      liqOutput?.meta.fallbackActive ?? false,
      gammaConfidence:     gammaConf,
      liquidityConfidence: liqConf,
      smcConfidence:       smcConf,
      triggerSources,
      candleTrapsActive:   traps.length,
    },
  };
}
